import React from 'react';
import { useApp } from '../context/AppContext';
import { triggerHaptic } from '../services/purrSynthesizer';
import { Compass, Flame, ArrowLeft, Shield, Eye } from 'lucide-react';

interface ContextBreadcrumbProps {
  realmName: string;
  deckTitle: string;
  realmEmoji?: string;
}

export const ContextBreadcrumb: React.FC<ContextBreadcrumbProps> = ({
  realmName,
  deckTitle,
  realmEmoji = '🌸'
}) => {
  const { profile, setActiveTab } = useApp();

  // Inviolable UX rule: Only render context navigation anchor for Echo (Admin).
  // Family, community, and guests maintain a clean, dedicated interface without admin clutter.
  if (profile !== 'echo') {
    return null;
  }

  const handleReturnToCockpit = () => {
    triggerHaptic([30, 20, 50]);
    setActiveTab('cockpit');
  };

  const handleJumpToStage = () => {
    triggerHaptic(40);
    setActiveTab('stagerig');
  };

  return (
    <div className="sticky top-2 z-40 mx-auto w-full max-w-lg px-2 pt-1 pb-2 pointer-events-auto animate-fade-in">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-full bg-slate-900/85 backdrop-blur-md border border-amber-500/30 shadow-[0_4px_20px_rgba(0,0,0,0.5)] text-xs font-mono">
        {/* Left: Realm & Deck Context */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm select-none">{realmEmoji}</span>
          <div className="flex items-center gap-1 truncate">
            <span className="text-amber-400/90 font-bold uppercase tracking-wider text-[10px]">
              {realmName}:
            </span>
            <span className="text-slate-200 text-[11px] truncate">
              {deckTitle}
            </span>
          </div>
        </div>

        {/* Right: Quick Action Escape Anchors */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={handleJumpToStage}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-400/30 active:scale-95 transition-all text-[10px] font-bold"
            title="Stage Rig & DMX / X32 Console"
          >
            <Flame className="w-3 h-3 text-amber-400 animate-pulse" />
            <span>Stage</span>
          </button>

          <button
            onClick={handleReturnToCockpit}
            className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-cyan-950/80 hover:bg-cyan-900/90 text-cyan-300 border border-cyan-400/40 active:scale-95 transition-all text-[10px] font-bold shadow-sm"
            title="Return to Homelab Cockpit"
          >
            <ArrowLeft className="w-3 h-3" />
            <span>Cockpit 🗝️</span>
          </button>
        </div>
      </div>
    </div>
  );
};
